import { useEffect, useMemo, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import ExpenseSearchSelect, { mapToSelectOptions } from "./ExpenseSearchSelect";
import * as XLSX from "xlsx";
import "./ExpenseModule.css";

const emptyForm = { expense_head_id: "", name: "", code: "", price: "", gst_percent: "", description: "" };

const fmtMoney = (v) => parseFloat(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const priceWithGst = (price, gstPercent) => {
    const p = parseFloat(price) || 0;
    const gst = parseFloat(gstPercent) || 0;
    return gst > 0 ? p * (1 + gst / 100) : p;
};

export default function ExpenseItemMasterList() {
    const { http, user } = AuthUser();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [codeFilter, setCodeFilter] = useState("");

    useEffect(() => {
        fetchCategories();
        fetchItems();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await http.get(`/expense_heads?login_user=${user.id}`);
            if (res.data.success) setCategories(res.data.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchItems = async () => {
        setIsLoading(true);
        try {
            const res = await http.get(`/expense_item_masters?login_user=${user.id}`);
            if (res.data.success) setItems(res.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Error fetching item masters");
        } finally {
            setIsLoading(false);
        }
    };

    const openAddModal = () => {
        setEditId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditId(item.id);
        setForm({
            expense_head_id: item.expense_head_id ? String(item.expense_head_id) : "",
            name: item.name || "",
            code: item.code || "",
            price: item.price !== null && item.price !== undefined ? String(item.price) : "",
            gst_percent: item.gst_percent !== null && item.gst_percent !== undefined ? String(item.gst_percent) : "",
            description: item.description || "",
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditId(null);
        setForm(emptyForm);
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.expense_head_id) { toast.error("Please select category"); return; }
        if (!form.name.trim()) { toast.error("Please enter item name"); return; }
        if (form.price !== "" && (isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0)) {
            toast.error("Please enter a valid price");
            return;
        }
        if (form.gst_percent !== "" && (isNaN(parseFloat(form.gst_percent)) || parseFloat(form.gst_percent) < 0 || parseFloat(form.gst_percent) > 100)) {
            toast.error("Please enter a valid GST %");
            return;
        }

        const duplicate = items.some((item) => {
            if (editId && item.id === editId) return false;
            return String(item.expense_head_id) === String(form.expense_head_id)
                && (item.name || "").trim().toLowerCase() === form.name.trim().toLowerCase();
        });
        if (duplicate) {
            toast.error("Item name already exists in this category");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                expense_head_id: parseInt(form.expense_head_id, 10),
                name: form.name.trim(),
                code: form.code.trim() || null,
                price: form.price === "" ? 0 : parseFloat(form.price),
                gst_percent: form.gst_percent === "" ? 0 : parseFloat(form.gst_percent),
                description: form.description.trim() || null,
            };
            const res = editId
                ? await http.put(`/expense_item_master/${editId}?login_user=${user.id}`, payload)
                : await http.post(`/expense_item_master?login_user=${user.id}`, payload);

            if (res.data.success) {
                toast.success(editId ? "Item updated!" : "Item added!");
                closeModal();
                fetchItems();
            } else {
                toast.error(res.data.message || "Error saving item");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error saving item");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this item master?")) return;
        try {
            const res = await http.delete(`/expense_item_master/${id}`);
            if (res.data.success) {
                toast.success("Deleted!");
                fetchItems();
            }
        } catch (err) {
            toast.error("Error deleting");
        }
    };

    const categoryOptions = mapToSelectOptions(categories);
    const filteredItems = useMemo(() => {
        const normalizedName = nameFilter.trim().toLowerCase();
        const normalizedCode = codeFilter.trim().toLowerCase();

        return items
            .filter((item) => {
                const matchesCategory = !categoryFilter
                    || String(item.expense_head_id) === categoryFilter;
                const matchesName = !normalizedName
                    || (item.name || "").toLowerCase().includes(normalizedName);
                const matchesCode = !normalizedCode
                    || (item.code || "").toLowerCase().includes(normalizedCode);

                return matchesCategory && matchesName && matchesCode;
            })
            .sort((a, b) => {
                const categoryComparison = (a.head?.name || "").localeCompare(
                    b.head?.name || "",
                    undefined,
                    { sensitivity: "base", numeric: true }
                );
                if (categoryComparison !== 0) return categoryComparison;

                const nameComparison = (a.name || "").localeCompare(
                    b.name || "",
                    undefined,
                    { sensitivity: "base", numeric: true }
                );
                if (nameComparison !== 0) return nameComparison;

                return (a.code || "").localeCompare(b.code || "", undefined, {
                    sensitivity: "base",
                    numeric: true,
                });
            });
    }, [items, categoryFilter, nameFilter, codeFilter]);

    const clearFilters = () => {
        setCategoryFilter("");
        setNameFilter("");
        setCodeFilter("");
    };

    const handleExport = () => {
        const exportData = filteredItems.map((item, index) => ({
            No: index + 1,
            Category: item.head?.name || "",
            Name: item.name || "",
            Code: item.code || "",
            Price: item.price ?? 0,
            "GST %": item.gst_percent ?? 0,
            "Price + GST": Number(priceWithGst(item.price, item.gst_percent).toFixed(2)),
            Description: item.description || "",
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Item Master");
        XLSX.writeFile(
            workbook,
            `Expense_Item_Master_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        toast.success("Item master exported successfully!");
    };

    return (
        <>
            <Toaster />
            <div className="container mt-3 expense-module">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Item Master</h5>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={handleExport}
                            disabled={filteredItems.length === 0}
                        >
                            <i className="fas fa-file-excel me-1"></i>Export
                        </button>
                        <button
                            type="button"
                            className="btn btn-success px-3 py-2 fw-semibold"
                            onClick={openAddModal}
                            disabled={categories.length === 0}
                            style={{
                                background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
                                border: "none",
                                boxShadow: "0 4px 12px rgba(46, 125, 50, 0.35)",
                                minWidth: "140px",
                            }}
                        >
                            <i className="fas fa-plus-circle me-2"></i>Add Item
                        </button>
                    </div>
                </div>

                {categories.length === 0 && (
                    <div className="alert alert-warning py-2 small mb-3">
                        No categories found. Please add categories first from the Category menu.
                    </div>
                )}

                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Category</label>
                                <ExpenseSearchSelect
                                    options={categoryOptions}
                                    value={categoryFilter}
                                    onChange={setCategoryFilter}
                                    placeholder="All categories"
                                    isClearable
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Name</label>
                                <input
                                    type="search"
                                    className="form-control form-control-sm"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    placeholder="Search item name..."
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Code</label>
                                <input
                                    type="search"
                                    className="form-control form-control-sm"
                                    value={codeFilter}
                                    onChange={(e) => setCodeFilter(e.target.value)}
                                    placeholder="Search item code..."
                                />
                            </div>
                            <div className="col-md-3 d-flex justify-content-between align-items-center">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                                    <i className="fas fa-times me-1"></i>Clear
                                </button>
                                <span className="badge bg-secondary">{filteredItems.length} records</span>
                            </div>
                        </div>
                    </div>
                </div>

                {isLoading ? <TaskSkeleton /> : (
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead className="table-primary">
                                <tr>
                                    <th style={{ width: "50px" }}>No</th>
                                    <th>Category</th>
                                    <th>Name</th>
                                    <th>Code</th>
                                    <th className="text-end">Price</th>
                                    <th className="text-end">GST %</th>
                                    <th className="text-end">Price + GST</th>
                                    <th>Description</th>
                                    <th style={{ width: "120px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted py-3">
                                            {items.length === 0
                                                ? "No items yet. Click Add Item to create one."
                                                : "No items match the selected filters."}
                                        </td>
                                    </tr>
                                ) : filteredItems.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td>{idx + 1}</td>
                                        <td className="fw-semibold text-primary">{item.head ? item.head.name : "—"}</td>
                                        <td>{item.name}</td>
                                        <td>{item.code || "—"}</td>
                                        <td className="text-end">{fmtMoney(item.price)}</td>
                                        <td className="text-end">{fmtMoney(item.gst_percent)}</td>
                                        <td className="text-end fw-semibold">{fmtMoney(priceWithGst(item.price, item.gst_percent))}</td>
                                        <td>{item.description || "—"}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-outline-primary px-2" onClick={() => openEditModal(item)} title="Edit">
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger px-2" onClick={() => handleDelete(item.id)} title="Delete">
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal d-block expense-module" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header py-2" style={{ background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)", color: "#fff" }}>
                                <h6 className="modal-title mb-0">
                                    <i className="fas fa-box me-2"></i>
                                    {editId ? "Edit Item" : "Add Item"}
                                </h6>
                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-semibold">Category *</label>
                                            <ExpenseSearchSelect
                                                options={categoryOptions}
                                                value={form.expense_head_id}
                                                onChange={(val) => setForm((prev) => ({ ...prev, expense_head_id: val }))}
                                                placeholder="Search category..."
                                                menuPortal
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-semibold">Item Name *</label>
                                            <input type="text" name="name" className="form-control form-control-sm" value={form.name} onChange={handleChange} placeholder="Item name" required autoFocus />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-semibold">Code</label>
                                            <input type="text" name="code" className="form-control form-control-sm" value={form.code} onChange={handleChange} placeholder="Item code (optional)" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-semibold">Price</label>
                                            <input type="number" step="0.01" min="0" name="price" className="form-control form-control-sm text-end" value={form.price} onChange={handleChange} placeholder="0.00" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-semibold">GST %</label>
                                            <input type="number" step="0.01" min="0" max="100" name="gst_percent" className="form-control form-control-sm text-end" value={form.gst_percent} onChange={handleChange} placeholder="0.00" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-semibold">Price + GST</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-end fw-semibold bg-light"
                                                value={fmtMoney(priceWithGst(form.price, form.gst_percent))}
                                                readOnly
                                                tabIndex={-1}
                                                title="Price including GST"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-semibold">Description</label>
                                            <textarea name="description" className="form-control form-control-sm" rows={2} value={form.description} onChange={handleChange} placeholder="Description (optional)" />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                                        {saving ? (
                                            <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                        ) : (
                                            <><i className="fas fa-check me-1"></i>{editId ? "Update" : "Save"}</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
